import type { FC, PropsWithChildren } from 'react';
import { createContext, useRef } from 'react';
import type { StoreApi } from 'zustand';

import { createAccountFiltersStoreWithInitials } from './AccountFiltersStore';
import type { AccountFiltersStore, InitialAccountFiltersStoreValues } from './AccountFiltersStore.types';
import AccountFiltersStoreUrlSynchronizer from './AccountFiltersStoreUrlSynchronizer';

type AccountFiltersContextStore = StoreApi<AccountFiltersStore> | undefined;

export const AccountFiltersContext = createContext<AccountFiltersContextStore>(undefined);

export type StoresProviderProps = PropsWithChildren<{ initialValues?: InitialAccountFiltersStoreValues }>;

/**
 * Use the URL and synchronizer as the only source of truth for this context. They automatically overwrite any direct
 * modifications made to the store once accessed one of synchronized Account slug subpages. Keep the store persistent by
 * ensuring that other components consume the store state and validate URL parameters for synchronized pages.
 *
 */

export const AccountFiltersStoreProvider: FC<StoresProviderProps> = ({ children, initialValues = {} }) => {
	const accountFiltersStoreRef = useRef<AccountFiltersContextStore>();

	const setupStore = () => {
		accountFiltersStoreRef.current = createAccountFiltersStoreWithInitials(initialValues);
	};

	if (!accountFiltersStoreRef.current) {
		setupStore();
	}

	return (
		<AccountFiltersContext.Provider value={accountFiltersStoreRef.current}>
			<AccountFiltersStoreUrlSynchronizer>{children}</AccountFiltersStoreUrlSynchronizer>
		</AccountFiltersContext.Provider>
	);
};
